import React from 'react';
import { useNavigate } from 'react-router-dom';
import RialSymbol from '../components/RialSymbol';
import {
  cartDisplayName,
  cartItemCount,
  cartSubtotal,
  useMenuCart,
  type RestaurantCart,
} from '../services/menuCart';
import PageHeader from '../components/ui/PageHeader';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  InfoOutlined as InfoIcon,
  Remove as RemoveIcon,
  RestaurantMenu as RestaurantMenuIcon,
  ShoppingCartOutlined as EmptyCartIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

/**
 * Review/edit the standalone menu cart before checkout.
 *
 * The cart can span several restaurants at once; items are grouped under the
 * restaurant they came from, and each group is placed as its own order at
 * checkout.
 */
export default function MenuCart() {
  const navigate = useNavigate();
  const cart = useMenuCart();

  if (cart.carts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <EmptyCartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Your cart is empty</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
          Add items from a restaurant's menu to get started.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/restaurants')}>
          Browse Restaurants
        </Button>
      </Box>
    );
  }

  const multipleRestaurants = cart.carts.length > 1;

  const renderRestaurant = (restaurantCart: RestaurantCart) => {
    const count = cartItemCount(restaurantCart);
    const subtotal = cartSubtotal(restaurantCart);
    return (
      <Card key={restaurantCart.businessId} sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <StorefrontIcon color="primary" fontSize="small" />
          <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
            {cartDisplayName(restaurantCart)}
          </Typography>
          <Button
            size="small"
            color="error"
            onClick={() => cart.clearRestaurant(restaurantCart.businessId)}
          >
            Remove
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {restaurantCart.lines.map(({ item, quantity }) => (
            <Box key={item._id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundImage: item.images?.[0] ? `url(${item.images[0]})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {!item.images?.[0] && <RestaurantMenuIcon sx={{ color: 'text.disabled' }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }} noWrap>
                  {item.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <RialSymbol /> {item.price.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" onClick={() => cart.removeItem(item._id)}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ width: 22, textAlign: 'center', fontWeight: 600 }}>
                  {quantity}
                </Typography>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => cart.addItem(item)}
                  disabled={quantity >= (item.inventory?.remainingQuantity ?? 0)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {count} item{count === 1 ? '' : 's'}
          </Typography>
          <Typography sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <RialSymbol /> {subtotal.toFixed(2)}
          </Typography>
        </Box>
      </Card>
    );
  };

  const overstocked = cart.lines.some(
    (l) => (l.item.inventory?.remainingQuantity ?? 0) < l.quantity
  );

  return (
    <Box>
      <PageHeader
        title="Your Cart"
        actions={
          <Button color="error" onClick={cart.clear}>
            Clear All
          </Button>
        }
      />

      {cart.carts.map(renderRestaurant)}

      <Card sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography color="text.secondary">
            {cart.itemCount} item{cart.itemCount === 1 ? '' : 's'}
            {multipleRestaurants ? ` · ${cart.carts.length} restaurants` : ''}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 18, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <RialSymbol /> {cart.total.toFixed(2)}
          </Typography>
        </Box>

        {multipleRestaurants && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <InfoIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Placed as {cart.carts.length} separate orders — one pickup code per restaurant.
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />
        {overstocked && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            One or more items exceed what's currently available.
          </Alert>
        )}
        <Button fullWidth variant="contained" size="large" onClick={() => navigate('/menu-checkout')}>
          Proceed to Checkout
        </Button>
      </Card>
    </Box>
  );
}
